import React, { useEffect, useState, useContext, useCallback } from 'react'
import { z } from '@/utils/myZodLite'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { LeadFormQuestionsView } from './instantForm/LeadFormQuestionsView'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { usePageData } from '../hooks'

import { SettingsContext } from '@/context/settings-context'
import { FormDescription } from './instantForm/FormDescription.jsx'
import { CharLimitedInput } from '@/components/common/CharLimitedInput'
import { isEmpty } from 'lodash'
import { useFormValidation } from '../hooks/useFormValidation'
import WizardSteps from '@/components/common/wizard/WizardSteps'

const Navigation = ({ goBack, handleNext }) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button className="tw:w-[156px] tw:!text-sm" onClick={handleNext}>
                Next
            </Button>
        </div>
    )
}

export const FormIntro = () => {
    const {
        goBack,
        goNext,
        getStepContentData,
        setStepContentData,
        registerCustomNavigation,
        clearCustomNavigation,
        dataForStepper,
    } = useWizard()
    const { connectedPublishers } = useMediaBuyingContext()
    const { theme } = useContext(SettingsContext)

    const textColor = theme === 'dark' ? 'text-white' : 'text-dark'

    const [hasBackgroundImage, setBackgroundImageOpt] = useState(false)
    const [hasGreeting, setHasGreeting] = useState(false)
    const [backgroundImage, setBackgroundImage] = useState('adImage')
    const [greeting, setGreeting] = useState({})
    const [headline, setHeadline] = useState('')
    const [description, setDescription] = useState(null)
    const [questions, setQuestions] = useState([])

    const { pageIcon, pageTitle } = usePageData(connectedPublishers)

    const emptyDescriptionObj = {
        descriptionType: 'paragraph',
        list: [],
        paragraph: '',
    }

    const FormSchema = z.object(
        {
            hasGreeting: z.boolean(false),
            headline: z.string(),
            description: z.object({
                descriptionType: z.string(),
                list: z.optional(z.array(z.string())),
                paragraph: z.optional(z.string()),
            }),
        },
        (data) => {
            const errors = []

            if (data.hasGreeting && isEmpty(data?.description?.paragraph) && isEmpty(data?.description?.list)) {
                errors.push({ message: 'Please add paragraph text.', path: ['paragraph'] })
                errors.push({ message: 'Please provide a list.', path: ['list'] })
            } else if (data.hasGreeting && !isEmpty(data?.description?.list)) {
                // Check if any list item is empty
                const hasEmptyItems = data.description.list.some((item) => isEmpty(item))
                if (hasEmptyItems) {
                    errors.push({
                        message: 'Please provide text for all list items.',
                        path: ['list[0]'],
                    })
                }
            }

            if (data.hasGreeting && isEmpty(data?.headline)) {
                errors.push({ message: 'Please provide a headline for the greeting.', path: ['headline'] })
            }
            return errors.length ? errors : true
        },
    )

    const { formErrors, validateForm: validate, hasErrors } = useFormValidation(FormSchema)

    const validateForm = (silent = false) => {
        return validate({ description, headline, hasGreeting }, silent)
    }

    useEffect(() => {
        registerCustomNavigation(
            <Navigation
                goBack={goBack}
                handleNext={() => {
                    const result = validate({ description, headline, hasGreeting }, false)
                    if (result) goNext()
                }}
            />,
        )

        return () => clearCustomNavigation()
    }, [description, headline, hasGreeting])

    useEffect(() => {
        setStepContentData({
            hasBackgroundImage,
            backgroundImage,
            hasGreeting,
            greeting,
            headline,
            description,
        })
    }, [hasGreeting, greeting, description, setStepContentData, backgroundImage, hasBackgroundImage, headline])

    useEffect(() => {
        const saved = getStepContentData() || {}
        const formQuestions = getStepContentData('formQuestions')
        setBackgroundImageOpt(saved.hasBackgroundImage)
        setHasGreeting(Boolean(saved.hasGreeting))
        setBackgroundImage(saved.backgroundImage || null)
        setGreeting(saved.greeting || false)
        setHeadline(saved.headline || '')
        setDescription(saved.description || emptyDescriptionObj)
        setQuestions(formQuestions?.questions || [])
    }, [])

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="formIntro" />
            <div className={`d-flex h-100 ${theme} campaigns-container w-100`}>
                <div className="border-right-white-1 tw:p-6">
                    <div className="tw:flex tw:items-center tw:gap-2">
                        <Checkbox
                            id="backgroundImage"
                            checked={hasBackgroundImage}
                            onCheckedChange={(checked) => {
                                if (checked === false) {
                                    setBackgroundImage(null) // Default to ad image when enabling background image
                                }
                                setBackgroundImageOpt(checked)
                            }}
                        />
                        <Label htmlFor="backgroundImage" className="tw:inline tw:justify-start tw:cursor-pointer">
                            <div className="tw:flex tw:gap-2">
                                <span
                                    className={`wizard-label tw:flex tw:text-sm tw:!ml-0 tw:font-semibold ${textColor}`}
                                >
                                    Background Image
                                </span>
                                <span className="tw:text-sm tw:font-semibold tw:text-gray-200">Optional</span>
                            </div>
                        </Label>
                    </div>
                    <div className="tw:pr-2 tw:pt-2 pb-4 tw:text-sm tw:leading-[150%]">
                        The image will be shown at the top of the screen and behind your form. Form rendering and
                        interaction may vary based on the device and other factors for optimal results.
                    </div>

                    {/*todo  [tim Jun 2025] - removed background image for 1st release.*/}

                    <div className="tw:flex tw:items-center tw:gap-2 tw:pt-1">
                        <Checkbox
                            id="greeting"
                            checked={hasGreeting}
                            onCheckedChange={(checked) => {
                                setHasGreeting(checked)
                                if (checked) {
                                    setDescription({ ...emptyDescriptionObj })
                                    setHeadline('')
                                }
                                if (hasErrors) {
                                    validateForm(true)
                                }
                            }}
                        />
                        <Label htmlFor="greeting" className="tw:inline tw:justify-start tw:cursor-pointer">
                            <div className="tw:flex tw:gap-2">
                                <span
                                    className={`wizard-label tw:flex tw:text-sm tw:!ml-0 tw:font-semibold ${textColor}`}
                                >
                                    Greeting
                                </span>
                                <span className="tw:text-sm tw:font-semibold tw:text-gray-200">Optional</span>
                            </div>
                        </Label>
                    </div>
                    <div className="tw:pr-2 tw:pt-2 pb-4 tw:text-sm">
                        Let people know any relevant details and why they should complete your form.
                    </div>
                    {hasGreeting && (
                        <div>
                            <div className="tw:mb-6 tw:flex tw:flex-col tw:gap-2">
                                <label className="tw:text-sm tw:font-semibold">Intro / Headline</label>
                                <CharLimitedInput
                                    value={headline}
                                    className={`${formErrors['headline'] ? 'tw:!border-red-500' : ''}`}
                                    onChange={(val) => {
                                        setHeadline(val)
                                        if (hasErrors) {
                                            validateForm(true)
                                        }
                                    }}
                                    placeholder="Enter a short headline"
                                />
                                {!isEmpty(formErrors['headline']) && (
                                    <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1 tw:absolute">
                                        {formErrors['headline']}
                                    </div>
                                )}
                            </div>
                            <FormDescription
                                descriptionValue={description}
                                onChange={(val) => {
                                    setDescription(val)
                                    if (hasErrors) {
                                        validateForm(true)
                                    }
                                }}
                                formErrors={formErrors}
                            />
                        </div>
                    )}
                </div>

                <div className="position-relative tw:p-6" style={{ width: 320, maxWidth: 320 }}>
                    <LeadFormQuestionsView
                        theme={theme}
                        questions={questions}
                        headline={headline}
                        description={description}
                        pageTitle={pageTitle}
                        pageIcon={pageIcon}
                        hideSelectForm={true}
                    />
                </div>
            </div>
        </div>
    )
}
